import QrCodeLib, { type QRCodeRenderersOptions } from 'qrcode';
import React from 'react';
import { Text } from '@applyboard/crystal-ui';
import { useTranslation } from '@applyboard/ui-utils';

export interface QrCodeProps {
  size?: number;
  url: string;
  ariaLabel: string;
}

const QR_CODE_OPTIONS = {
  errorCorrectionLevel: 'L',
};

export const QrCode = React.forwardRef<unknown, QrCodeProps>(function QrCode(
  { size = 260, url, ariaLabel },
  ref,
) {
  const [hasError, setHasError] = React.useState(false);
  const qrDiv = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const options = React.useMemo(
    () => ({
      ...QR_CODE_OPTIONS,
      width: size,
    }),
    [size],
  );

  const createQrCode = React.useCallback(
    (url: string) => {
      QrCodeLib.toCanvas(url, options as QRCodeRenderersOptions, function (err, canvas) {
        if (err) {
          setHasError(true);
          return;
        }
        canvas.setAttribute('aria-label', ariaLabel);
        const el = qrDiv.current;
        while (el?.firstChild) {
          if (el.lastChild) {
            el.removeChild(el.lastChild);
          }
        }
        qrDiv.current?.appendChild(canvas);
      });
    },
    [ariaLabel, options],
  );

  React.useEffect(() => {
    if (ref) {
      // @ts-expect-error Not yet sure how to type this
      ref.current = {
        download: async () =>
          new Promise(resolve => {
            QrCodeLib.toDataURL(url, options as QRCodeRenderersOptions, (err, dataUrl) =>
              resolve(err ? null : dataUrl),
            );
          }),
      };
    }
  }, [options, ref, url]);

  React.useEffect(() => {
    if (qrDiv.current) {
      createQrCode(url);
    }
  }, [createQrCode, qrDiv, url]);

  if (hasError) {
    return <Text>{t('hub.qrCode.renderError')}</Text>;
  }

  return <div ref={qrDiv} />;
});
